/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable react/prop-types,react/no-unused-state */
import IconArrowLeft from '@mui/icons-material/esm/ArrowBackIos';
import IconArrowRight from '@mui/icons-material/esm/ArrowForwardIos';
import IconEdit from '@mui/icons-material/esm/DeleteForever';
import GradeIcon from '@mui/icons-material/esm/Grade';
import {Box, IconButton, Tooltip} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import React, {useEffect, useState} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {intl} from '../../helper/IntlGlobalProvider';
import {Path} from '../../helper/Path';
import theme from '../../theme';
import {withUser} from '../../UserProvider';
import {getHelpURL} from '../../utils/help';
import {removeFromFavorites} from '../app/AppGraphQL';
import {useQuery} from '../app/GraphQLWSClient';
import NewAppDialog from '../app/NewAppDialog';
import * as Query from '../app/queries';
import {StreamSheetAppBar} from '../StreamSheetAppBar';
import StreamSheetNavigation from '../StreamSheetNavigation';
import {AppAuth} from '../utils/Authorization';
import {useAsyncReference} from '../utils/JSGHooks';
import StreamSheetBreadCrumbs from '../utils/StreamSheetBreadCrumbs';
import StreamSheetPageTitle from '../utils/StreamSheetPageTitle';
import StreamSheetPaper from '../utils/StreamSheetPaper';

const drawerWidth = 60;
const templatesG = [];
let newsG = [];
// let favoritesG = [];

const svgToImage = (svg) => {
	const blob = new Blob([svg], { type: 'image/svg+xml' });
	const svgUrl = URL.createObjectURL(blob);
	const image = document.createElement('img');
	image.src = svgUrl;
	image.addEventListener('load', () => URL.revokeObjectURL(svgUrl), { once: true });
	return image;
};

function HomePageComponent(props) {
	const { favoritesG } = props;
	const navigate = useNavigate();
	const [templates, setTemplates] = useState([]);
	const [news, setNews] = useState([]);
	const [favorites, setFavorites] = useState([]);
	const [editIndex, setEditIndex] = useState(-1);
	const [templateOffset, setTemplateOffset] = useState(0);
	const [newsOffset, setNewsOffset] = useState(0);
	const [favsOffset, setFavsOffset] = useState(0);
	const [showNewAppFromTemplate, setShowNewAppFromTemplate] = useState({ open: false, templateId: null });
	const [templatesNotAvailable, setTemplatesNotAvailable] = useState(false);
	const [tileSize] = useAsyncReference({ cx: 300, cy: 200 });

	const updateFavorites = () => {
		const tc = document.getElementById('favoritesContainer');
		const favoriteBoxCount = tc ? Math.max(1, Math.floor(tc.clientWidth / (tileSize.current.cx + 20)) - 1) : 1;
		const tpls = [];

		for (let i = 0; i < favoriteBoxCount; i += 1) {
			if (i + favsOffset < favoritesG.length) {
				const fav = favoritesG[i + favsOffset];
				fav.image = fav.svg ? svgToImage(fav.svg) : undefined;
				tpls.push(fav);
			}
		}

		setFavorites(tpls);
	};

	const updateTemplates = () => {
		const tc = document.getElementById('templateContainer');
		const templateBoxCount = tc ? Math.floor(tc.clientWidth / (tileSize.current.cx + 20)) : 1;
		const tpls = [];

		for (let i = 0; i < templateBoxCount; i += 1) {
			if (i + templateOffset < templatesG.length) {
				const tmpl = templatesG[i + templateOffset];
				tmpl.image = tmpl.svg ? svgToImage(tmpl.svg) : undefined;
				tpls.push(tmpl);
			}
		}

		setTemplates(tpls);
	};

	const updateNews = () => {
		const tc = document.getElementById('newsContainer');
		const boxCount = tc ? Math.floor(tc.clientWidth / (tileSize.current.cx + 20)) : 1;
		const tpls = [];

		for (let i = 0; i < boxCount; i += 1) {
			if (i + newsOffset < newsG.length) {
				tpls.push(newsG[i + newsOffset]);
			}
		}

		setNews(tpls);
	};

	const update = () => {
		updateFavorites();
		updateTemplates();
		updateNews();
	};

	useEffect(() => updateFavorites(), [favoritesG, favsOffset]);
	useEffect(() => updateTemplates(), [templateOffset]);
	useEffect(() => updateNews(), [newsOffset]);

	const templatesQuery = useQuery(Query.templates);

	const onCreateFromTemplate = (templateId) => setShowNewAppFromTemplate({ open: true, templateId });

	useEffect(() => {
		if (templatesQuery.status === 'done' && templatesQuery.result.templates) {
			if (templatesQuery.result.templates.length === 0) {
				setTemplatesNotAvailable(true);
			}
			templatesG.length = 0;
			templatesQuery.result.templates.forEach((template, index) => {
				templatesG[index] = {
					id: template.id,
					label: template.name,
					svg: template.image,
					isPro: template.isPro,
					canIUse: template.canIUse,
					description: template.description || 'Missing Description',
					category: template.category
				};
			});

			updateTemplates();
		} else if (templatesQuery.status === 'error') {
			templatesG.length = 0;
			templatesG.push({
				id: 0,
				label: 'Templates not available'
			});
			updateTemplates();
			console.error('Failed to fetch templates!', ...templatesQuery.result);
		}
	}, [templatesQuery]);

	useEffect(() => {
		const resizeObserver = new ResizeObserver(update);
		const tc = document.getElementById('templateContainer');
		if (tc) {
			resizeObserver.observe(tc);
		}

		updateFavorites();

		const url = 'https://cedalo.com/wp-json/wp/v2/posts/?per_page=10';

		fetch(url)
			.then((resp) => resp.json())
			.then((data) => {
				newsG.length = 0;
				newsG = data.map((newR) => {
					const elem = document.createElement('div');
					elem.innerHTML = newR.excerpt.rendered;
					const elemTitle = document.createElement('div');
					elemTitle.innerHTML = newR.title.rendered;
					return {
						id: newR.id,
						label: elemTitle.textContent,
						ref: newR.link,
						content: elem.textContent
					};
				});
				updateNews();
			})
			.catch(() => {
				newsG.push({
					label: 'News not available',
					content: ''
				});
				updateNews();
			});

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	document.title = intl.formatMessage({ id: 'TabTitle.Home' }, {});

	return (
		<div style={{ width: 'inherit', height: 'inherit' }}>
			<div>
				<StreamSheetAppBar />
				<StreamSheetNavigation openPage={navigate} />
				{props.canAddApp ? (
					<NewAppDialog
						open={showNewAppFromTemplate.open}
						openPage={navigate}
						template={showNewAppFromTemplate.templateId}
						onClose={() => setShowNewAppFromTemplate({ open: false })}
					/>
				) : null}
			</div>
			<Box
				sx={{
					position: 'relative',
					height: 'calc(100% - 50px)',
					width: `calc(100% - ${drawerWidth}px)`,
					marginLeft: `${drawerWidth}px`,
					overflow: 'hidden',
					bgcolor: theme.wall.backgroundColor
				}}
			>
				<StreamSheetBreadCrumbs path="Home" openPage={navigate} />
				<StreamSheetPaper data-tour="page-home">
					<div
						id="pageContainer"
						style={{
							height: '100%',
							width: 'calc(100% - 32px)'
						}}
					>
						<StreamSheetPageTitle
							title={
								<FormattedMessage
									id="HomePage.Title"
									defaultMessage="Spreadsheet Apps for Stream Processing"
								/>
							}
						/>
						<Box sx={{ height: 'calc(100% - 75px)', overflowY: 'auto' }}>
							<Box id="favoritesContainer" sx={{ display: 'flex', flexWrap: 'wrap' }}>
								<Box sx={{ display: 'flex', flexDirection: 'column' }}>
									<Typography
										data-tour="home-favorites"
										color="text.primary"
										variant="h6"
										sx={{
											fontWeight: '400',
											width: 'calc(100% - 30px)',
											mt: '10px'
										}}
									>
										<FormattedMessage id="HomePage.Favorites" defaultMessage="Favorites" />
									</Typography>
									<Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
										{favorites.map((item, index) => (
											<Box
												sx={{
													margin: '10px 20px 10px 0px',
													cursor: 'pointer',
													width: `${tileSize.current.cx}px`,
													height: `${tileSize.current.cy}px`,
													display: 'inline-flex',
													borderRadius: '5px',
													backgroundColor:
														theme.palette.mode === 'dark' ? '#504e4e' : '#F7F9FC'
												}}
												xs={3}
												item
												key={item.id}
												onClick={() =>
													item.link ? navigate(item.link) : window.open(item.ref, '_blank')
												}
												onMouseOver={() => {
													if (item.id) {
														setEditIndex(index);
													}
												}}
												onMouseOut={() => {
													setEditIndex(-1);
												}}
											>
												<Box
													sx={{
														width: '35px',
														display: 'inline-flex',
														alignItems: 'center'
													}}
												>
													{index === 0 && favsOffset ? (
														<IconButton
															key="start"
															style={{
																left: '7px',
																display: 'inline-flex'
															}}
															size="small"
															onClick={(event) => {
																setFavsOffset((current) => current - 1);
																updateFavorites();
																event.stopPropagation();
															}}
														>
															<IconArrowLeft />
														</IconButton>
													) : null}
												</Box>
												<Box
													sx={{
														display: 'inline-block'
														// width: `${tileSize.current.cx - 70}px`
													}}
													item
													xs={3}
												>
													<div
														style={{
															display: 'flex',
															height: '40px',
															justifyContent: 'space-between'
														}}
													>
														<Typography
															color="text.primary"
															sx={{
																fontWeight: 'bold',
																paddingLeft: '10px',
																paddingTop: '10px',
																width: '220px',
																marginLeft: '-30px',
																overflow: 'hidden',
																textOverflow: 'ellipsis',
																whiteSpace: 'nowrap'
															}}
														>
															{item.scope ? (
																<span>
																	{item.label} - <i>{item.scope}</i>
																</span>
															) : (
																item.label
															)}
														</Typography>
														<IconButton
															key="stop"
															style={{
																top: '3px',
																left: '30px',
																visibility: editIndex === index ? 'visible' : 'hidden'
															}}
															size="small"
															onClick={async (event) => {
																event.stopPropagation();
																try {
																	await removeFromFavorites(item.id);
																	if (
																		favsOffset &&
																		favorites.length + favsOffset ===
																			favoritesG.length
																	) {
																		setFavsOffset((current) => current - 1);
																	}
																} catch (error) {
																	console.error(error);
																}
															}}
														>
															<IconEdit />
														</IconButton>
													</div>
													<div
														style={{
															height: '160px',
															flexDirection: 'column',
															display: 'flex',
															alignItems: 'center'
														}}
													>
														<img
															style={{ height: '90%', width: '100%' }}
															alt="EN-US"
															src={item.image ? item.image.src : 'images/app.svg'}
														/>
													</div>
												</Box>
												<Box
													sx={{
														width: '35px',
														display: 'inline-flex',
														alignItems: 'center'
													}}
												>
													{index === favorites.length - 1 &&
													favoritesG.length > favorites.length &&
													favorites.length + favsOffset < favoritesG.length ? (
														<IconButton
															key="stop"
															style={{
																display: 'inline-flex'
															}}
															size="small"
															onClick={(event) => {
																setFavsOffset((current) => current + 1);
																event.stopPropagation();
															}}
														>
															<IconArrowRight />
														</IconButton>
													) : null}
												</Box>
											</Box>
										))}
									</Box>
								</Box>
								<Box sx={{ display: 'flex', flexDirection: 'column' }}>
									<Typography
										data-tour="home-learn"
										color="text.primary"
										variant="h6"
										sx={{ fontWeight: '400', mt: '10px' }}
									>
										<FormattedMessage id="HomePage.Learn" defaultMessage="Learn" />
									</Typography>
									<Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
										<Box
											onClick={() => window.open(getHelpURL('introduction'), '_blank')}
											sx={{
												cursor: 'pointer',
												margin: '10px 20px 10px 0px'
											}}
											item
										>
											<Box
												sx={{
													width: `${tileSize.current.cx}px`,
													height: `${tileSize.current.cy}px`,
													borderRadius: '5px',
													backgroundColor:
														theme.palette.mode === 'dark' ? '#504e4e' : '#F7F9FC'
												}}
												item
											>
												<div style={{ height: '100%' }}>
													<img alt="EN-US" src="images/learn.svg" />
												</div>
											</Box>
										</Box>
									</Box>
								</Box>
							</Box>
							<Typography
								data-tour="home-templates"
								color="text.primary"
								variant="h6"
								sx={{ fontWeight: '400', mt: '10px' }}
							>
								<FormattedMessage id="HomePage.Templates" defaultMessage="Templates" />
							</Typography>
							<Box id="templateContainer">
								<Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
									{templatesNotAvailable ? (
										<Typography color="text.primary" variant="subtitle" sx={{ margin: '20px 0px' }}>
											<FormattedMessage
												id="HomePage.TemplatesNotAvailable"
												defaultMessage="Templates could not be loaded!"
											/>
										</Typography>
									) : null}
									{templates.map((item, index) => (
										<Box
											sx={{
												margin: '10px 20px 10px 0px',
												cursor: 'pointer',
												width: `${tileSize.current.cx}px`,
												height: `${tileSize.current.cy}px`,
												display: 'inline-flex',
												borderRadius: '5px',
												backgroundColor: theme.palette.mode === 'dark' ? '#504e4e' : '#F7F9FC'
											}}
											xs={3}
											item
											key={item.id}
											onClick={props.canAddApp ? () => onCreateFromTemplate(item.id) : undefined}
											// item.link ? navigate(item.link) : window.open(item.ref, '_blank')
										>
											<Box
												sx={{
													width: '35px',
													display: 'inline-flex',
													alignItems: 'center'
												}}
											>
												{index === 0 && templateOffset ? (
													<IconButton
														key="start"
														style={{
															left: '7px',
															display: 'inline-flex'
														}}
														size="small"
														onClick={(event) => {
															setTemplateOffset((current) => current - 1);
															event.stopPropagation();
														}}
													>
														<IconArrowLeft />
													</IconButton>
												) : null}
											</Box>
											<Box
												sx={{
													display: 'inline-block',
													width: `${tileSize.current.cx - 70}px`
												}}
												item
												xs={3}
											>
												<div
													style={{
														display: 'flex',
														height: '40px',
														justifyContent: 'space-between'
													}}
												>
													<Typography
														color="text.primary"
														sx={{
															fontWeight: 'bold',
															paddingTop: '10px',
															paddingLeft: '10px',
															marginLeft: '-30px',
															textOverflow: 'ellipsis',
															whiteSpace: 'nowrap',
															overflow: 'hidden'
														}}
													>
														{item.label}
													</Typography>
													{item.isPro ? (
														<Tooltip
															enterDelay={300}
															title={
																<FormattedMessage
																	id="HomePage.ProTemplate"
																	defaultMessage="Pro Template"
																/>
															}
														>
															<IconButton
																key="stop"
																style={{
																	display: 'inline-flex',
																	marginRight: '-30px',
																	marginTop: '5px',
																	height: '30px'
																}}
																size="small"
																onClick={(event) => {
																	event.stopPropagation();
																}}
															>
																<GradeIcon
																	sx={{
																		color: theme.palette.primary.main
																	}}
																/>
															</IconButton>
														</Tooltip>
													) : null}
												</div>
												<div
													style={{
														height: '110px',
														flexDirection: 'column',
														display: 'flex',
														alignItems: 'center'
													}}
												>
													<img
														style={{ height: '100%', width: '100%' }}
														alt="EN-US"
														src={item.image ? item.image.src : 'images/template.svg'}
													/>
												</div>
												<div
													style={{
														display: 'flex',
														height: '50px',
														justifyContent: 'space-between'
													}}
												>
													<Typography
														color="text.primary"
														sx={{
															overflow: 'hidden',
															display: '-webkit-box',
															WebkitLineClamp: '6',
															WebkitBoxOrient: 'vertical',
															fontSize: '0.75rem'
														}}
													>
														{item.description}
													</Typography>
												</div>
											</Box>
											<Box
												sx={{
													width: '35px',
													display: 'inline-flex',
													alignItems: 'center'
												}}
											>
												{index === templates.length - 1 &&
												templatesG.length > templates.length &&
												templates.length + templateOffset < templatesG.length ? (
													<IconButton
														key="stop"
														style={{
															display: 'inline-flex'
														}}
														size="small"
														onClick={(event) => {
															setTemplateOffset((current) => current + 1);
															event.stopPropagation();
														}}
													>
														<IconArrowRight />
													</IconButton>
												) : null}
											</Box>
										</Box>
									))}
								</Box>
							</Box>
							<Typography
								data-tour="home-news"
								color="text.primary"
								variant="h6"
								sx={{ fontWeight: '400', mt: '10px' }}
							>
								<FormattedMessage id="HomePage.News" defaultMessage="News" />
							</Typography>
							<Box id="newsContainer">
								<Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
									{news.map((item, index) => (
										<Box
											sx={{
												margin: '10px 20px 10px 0px',
												cursor: 'pointer',
												width: `${tileSize.current.cx}px`,
												height: `${tileSize.current.cy}px`,
												display: 'inline-flex',
												borderRadius: '5px',
												backgroundColor: theme.palette.mode === 'dark' ? '#504e4e' : '#F7F9FC'
											}}
											item
											xs={3}
											onClick={() => window.open(item.ref, '_blank')}
											key={item.id}
										>
											<Box
												sx={{
													width: '35px',
													display: 'inline-flex',
													alignItems: 'center'
												}}
											>
												{index === 0 && newsOffset ? (
													<IconButton
														key="start"
														style={{
															left: '7px',
															display: 'inline-flex'
														}}
														size="small"
														onClick={(event) => {
															setNewsOffset((current) => current - 1);
															event.stopPropagation();
														}}
													>
														<IconArrowLeft />
													</IconButton>
												) : null}
											</Box>
											<Box
												sx={{
													display: 'inline-block',
													width: `${tileSize.current.cx - 70}px`
												}}
												item
												xs={3}
											>
												<Typography
													color="text.primary"
													sx={{
														fontWeight: 'bold',
														paddingTop: '10px',
														height: '45px',
														marginLeft: '-20px',
														overflow: 'hidden',
														display: '-webkit-box',
														WebkitLineClamp: '2',
														WebkitBoxOrient: 'vertical'
													}}
												>
													{item.label}
												</Typography>
												<Typography
													color="text.primary"
													sx={{
														paddingTop: '10px',
														overflow: 'hidden',
														display: '-webkit-box',
														WebkitLineClamp: '6',
														WebkitBoxOrient: 'vertical',
														fontSize: '0.75rem'
													}}
												>
													{item.content}
												</Typography>
											</Box>
											<Box
												sx={{
													width: '35px',
													display: 'inline-flex',
													alignItems: 'center'
												}}
											>
												{index === news.length - 1 &&
												newsG.length > news.length &&
												news.length + newsOffset < newsG.length ? (
													<IconButton
														key="stop"
														style={{
															display: 'inline-flex'
														}}
														size="small"
														onClick={(event) => {
															setNewsOffset((current) => current + 1);
															event.stopPropagation();
														}}
													>
														<IconArrowRight />
													</IconButton>
												) : null}
											</Box>
										</Box>
									))}
									{news.length ? null : (
										<CircularProgress style={{ height: '50px', width: '50px', margin: '40px' }} />
									)}
								</Box>
							</Box>
						</Box>
					</div>
				</StreamSheetPaper>
			</Box>
		</div>
	);
}

export const HomePage = withUser(({ machines, user: { favorites, scopes } }) => ({
	canAddApp: AppAuth.canAddAny(scopes),
	favoritesG: favorites.length
		? favorites
				.map((favorite) => {
					const machine = machines.find((a) => a.id === favorite);
					const scope = scopes.length > 1 ? scopes.find((a) => a.id === machine.scope.id) : undefined;

					return machine
						? {
								id: machine.id,
								label: machine.name,
								svg: machine.settings.image ? machine.settings.image.content : '',
								link: Path.app(machine.id),
								scope: scope ? scope.name : undefined
						  }
						: undefined;
				})
				.filter((x) => !!x)
		: [
				{
					id: 0,
					label: intl.formatMessage({ id: 'HomePage.FavoritesAdd' }, {}),
					ref: getHelpURL('guides/app/#favorite')
				}
		  ]
}))(injectIntl(HomePageComponent));
